import React from "react";

import { monthNames } from "../MonthCalendar";
import { CalendarEvent } from "pages/Dashboard/CalendarDayCard";

type FilterColors = "sky" | "emerald" | "indigo" | "rose" | "amber";

interface ScheduleDayrProps {
  day: number;
  events: CalendarEvent[];
  dayDate: Date;
  onEventClick?: (event: CalendarEvent) => void;
  rowHeight?: "small" | "medium" | "full";
}
const heightClasses = {
  small: "h-36",
  medium: "h-60",
  full: "h-auto",
} as const;

export const ScheduleDay = ({ events, day, onEventClick, dayDate, rowHeight = "small" }: ScheduleDayrProps) => {
  const eventColor = (color: FilterColors) => {
    switch (color) {
      case "sky":
        return "text-white bg-sky-500 hover:bg-sky-600";
      case "indigo":
        return "text-white bg-indigo-500 hover:bg-indigo-600";
      case "amber":
        return "text-white bg-amber-500 hover:bg-amber-600";
      case "emerald":
        return "text-white bg-emerald-500 hover:bg-emerald-600";
      case "rose":
        return "text-white bg-rose-400 hover:bg-rose-500";
      default:
        return "bg-white dark:bg-slate-800 dark:text-slate-300 dark:hover:bg-slate-900 hover:bg-slate-100";
    }
  };

  return (
    <div className={`relative bg-white dark:bg-slate-800 ${heightClasses[rowHeight]} w-36`}>
      <div className="flex h-full flex-col justify-between">
        <div className="tiny-scrollbar relative flex grow flex-col overflow-y-auto p-0.5 sm:p-1.5">
          {events
            .sort((a, b) => a.eventStart.getTime() - b.eventStart.getTime())
            .map((event) => {
              return (
                <button
                  className="relative mb-1 w-full text-left"
                  key={event.key}
                  onClick={() => {
                    if (onEventClick) onEventClick(event);
                  }}
                >
                  <div className={`overflow-hidden rounded px-2 py-0.5 ${eventColor(event.eventColor)}`}>
                    <div className="flex items-center truncate text-xs font-semibold">
                      {event?.icon && event.icon}
                      {event.eventName}
                    </div>
                    {event?.eventDescription && <div className="truncate text-xs">{event.eventDescription}</div>}
                    {event.rootObject?.trainersPrices && event.rootObject.trainersPrices.length > 0 && (
                      <div className="truncate text-xs">
                        {event.rootObject.trainersPrices
                          .map((trainerPrice) => `${trainerPrice.trainerFirstName} ${trainerPrice.trainerSurname}`)
                          .join(", ")}
                      </div>
                    )}
                    {/* Event time */}
                    <div className="hidden truncate text-xs uppercase sm:block">
                      {/* Start date */}
                      {event.eventStart && (
                        <span>
                          {event.eventStart.toLocaleTimeString("pl-PL", {
                            hour: "2-digit",
                            minute: "2-digit",
                          })}
                        </span>
                      )}
                      {/* End date */}
                      {event.eventEnd && (
                        <span>
                          {" "}
                          &ndash;{" "}
                          <span>
                            {event.eventEnd.toLocaleTimeString("pl-PL", {
                              hour: "2-digit",
                              minute: "2-digit",
                            })}
                          </span>
                        </span>
                      )}
                    </div>
                  </div>
                </button>
              );
            })}
          <div
            className="pointer-events-none absolute bottom-0 left-0 right-0 h-4 bg-gradient-to-t from-white to-transparent dark:from-slate-800"
            aria-hidden="true"
          ></div>
        </div>
        <div className="flex items-center justify-between p-0.5 sm:p-1.5">
          {monthNames[dayDate.getMonth()]}
          <button
            className={`ml-auto inline-flex h-6 w-6 items-center justify-center rounded-full text-center text-xs font-medium hover:bg-indigo-100 sm:text-sm dark:text-slate-300 dark:hover:bg-slate-600`}
          >
            {day}
          </button>
        </div>
      </div>
    </div>
  );
};
